export type CycleFlow = 'none' | 'light' | 'medium' | 'heavy';

export interface CycleProfile {
  ownerUid: string;
  ownerName: string;
  ownerGender?: 'female' | 'male';
  enabled: boolean;
  lastPeriodStart: string;
  cycleLength: number;
  periodLength: number;
  flow: CycleFlow;
  energy: string;
  symptoms: string[];
  note: string;
  updatedAt?: any;
}

export interface CycleState {
  profiles?: Record<string, CycleProfile>;
  support?: Record<string, {
    fromUid: string;
    fromName: string;
    message: string;
    createdAt?: any;
  }>;
}

const DAY_MS = 86400000;

export function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseDateKey(key: string) {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function dateKeyFromDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function diffDays(from: Date, to: Date) {
  const a = new Date(from);
  const b = new Date(to);
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

export function clampCycle(value: number, fallback: number, min: number, max: number) {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

export function formatCycleDate(key: string) {
  if (!key) return 'Henüz yok';
  return parseDateKey(key).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

export function defaultCycleProfile(uid: string, name: string): CycleProfile {
  return {
    ownerUid: uid,
    ownerName: name,
    enabled: true,
    lastPeriodStart: todayKey(),
    cycleLength: 28,
    periodLength: 5,
    flow: 'medium',
    energy: 'Normal',
    symptoms: [],
    note: '',
  };
}

export function getCycleInfo(profile?: CycleProfile | null) {
  if (!profile?.enabled || !profile.lastPeriodStart) {
    return {
      active: false,
      phase: 'empty',
      title: 'Döngü kurulmadı',
      detail: 'Kendi döngüsünü ekleyince burada görünür.',
      day: 0,
      cycleLength: 28,
      periodLength: 5,
      nextStartKey: '',
      daysToNext: 0,
      progress: 0,
      accent: '#BF5AF2',
      supportHint: 'Kısa bir kurulum yeter.',
    };
  }

  const cycleLength = clampCycle(profile.cycleLength, 28, 21, 45);
  const periodLength = clampCycle(profile.periodLength, 5, 2, 10);
  const start = parseDateKey(profile.lastPeriodStart);
  const today = parseDateKey(todayKey());
  const rawDiff = diffDays(start, today);
  const cycleIndex = rawDiff >= 0 ? Math.floor(rawDiff / cycleLength) : -1;
  const day = rawDiff >= 0 ? ((rawDiff % cycleLength) + 1) : 1;
  const nextStart = rawDiff < 0 ? start : addDays(start, (cycleIndex + 1) * cycleLength);
  const nextStartKey = dateKeyFromDate(nextStart);
  const daysToNext = Math.max(0, diffDays(today, nextStart));
  const ovulationDay = Math.max(periodLength + 3, cycleLength - 14);
  const fertileStart = Math.max(periodLength + 1, ovulationDay - 5);
  const fertileEnd = Math.min(cycleLength, ovulationDay + 1);

  if (day <= periodLength) {
    return {
      active: true,
      phase: 'period',
      title: 'Regl dönemi',
      detail: `${day}. gün. Planları biraz daha yumuşak tutmak iyi gelebilir.`,
      day,
      cycleLength,
      periodLength,
      nextStartKey,
      daysToNext,
      progress: Math.min(100, Math.round((day / cycleLength) * 100)),
      accent: '#E91E8C',
      supportHint: 'Bugün sıcak, sakin ve anlayışlı kal.',
    };
  }

  if (day >= fertileStart && day <= fertileEnd) {
    return {
      active: true,
      phase: 'fertile',
      title: 'Verimli pencere tahmini',
      detail: `${day}. gün. Bu yalnızca döngü tahmini; kesin tıbbi veri değildir.`,
      day,
      cycleLength,
      periodLength,
      nextStartKey,
      daysToNext,
      progress: Math.min(100, Math.round((day / cycleLength) * 100)),
      accent: '#22D3EE',
      supportHint: 'Bedeni ve enerjiyi zorlamadan takip edin.',
    };
  }

  if (day >= cycleLength - 5) {
    return {
      active: true,
      phase: 'pms',
      title: 'Yaklaşan dönem',
      detail: `${daysToNext} gün sonra adet başlangıcı bekleniyor.`,
      day,
      cycleLength,
      periodLength,
      nextStartKey,
      daysToNext,
      progress: Math.min(100, Math.round((day / cycleLength) * 100)),
      accent: '#F59E0B',
      supportHint: 'Daha az baskı, daha çok nazik hatırlama.',
    };
  }

  return {
    active: true,
    phase: 'regular',
    title: 'Dengeli günler',
    detail: `${day}. gün. Sıradaki adet tahmini ${formatCycleDate(nextStartKey)}.`,
    day,
    cycleLength,
    periodLength,
    nextStartKey,
    daysToNext,
    progress: Math.min(100, Math.round((day / cycleLength) * 100)),
    accent: '#8B5CF6',
    supportHint: 'Ritmi bilmek bile planları kolaylaştırır.',
  };
}
